// @ts-nocheck
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Calendar, Clock, AlertTriangle, DollarSign, FileText, CheckCircle, X, ChevronRight, TestTube, Smartphone, Cake, Trash2 } from 'lucide-react';
import IOSPWAInstallGuide from './IOSPWAInstallGuide';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNotifications as useNotificationContext } from '@/contexts/NotificationContext';
import { useNotifications, Notification as SystemNotification } from '@/hooks/useNotifications';
import { notificationService } from '@/services/notificationService';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface NotificationDropdownProps {
  className?: string;
}

const getNotificationIcon = (type: string, icon?: string) => {
  // Se tem ícone personalizado (emoji), usa ele
  if (icon && icon.includes('🎂')) {
    return <span className="text-lg">{icon}</span>;
  }
  
  switch (type) {
    case 'appointment':
      return <Calendar className="h-4 w-4 text-blue-500" />;
    case 'task':
      return <Clock className="h-4 w-4 text-orange-500" />;
    case 'payment':
      return <DollarSign className="h-4 w-4 text-green-500" />;
    case 'document':
      return <FileText className="h-4 w-4 text-purple-500" />;
    case 'case':
      return <FileText className="h-4 w-4 text-indigo-500" />;
    case 'reminder':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    case 'birthday':
      return <Cake className="h-4 w-4 text-pink-500" />;
    default:
      return <Bell className="h-4 w-4 text-gray-500" />;
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'urgent':
      return 'border-red-500 bg-red-50';
    case 'high':
      return 'border-orange-500 bg-orange-50';
    case 'normal':
      return 'border-blue-500 bg-blue-50';
    case 'low':
      return 'border-gray-500 bg-gray-50';
    default:
      return 'border-gray-500 bg-gray-50';
  }
};

export const NotificationDropdown = ({ className }: NotificationDropdownProps) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [hasNotificationPermission, setHasNotificationPermission] = useState(false);
  const [isNotificationSupported, setIsNotificationSupported] = useState(false);
  const [isTestingNotification, setIsTestingNotification] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  
  // Detectar iOS
  const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                      (window.navigator as any).standalone === true;
  // Notificações do sistema existente (compromissos, tarefas, pagamentos)
  const {
    notifications: systemNotifications,
    unreadCount: systemUnreadCount,
    loading: systemLoading,
    markAsRead: systemMarkAsRead,
    markAllAsRead: systemMarkAllAsRead,
    deleteNotification: systemDeleteNotification,
    clearAll: systemClearAll,
    getTimeAgo
  } = useNotifications();

  // Notificações do nosso contexto (aniversários, etc.)
  const {
    notifications: contextNotifications,
    unreadCount: contextUnreadCount,
    markAsRead: contextMarkAsRead,
    markAllAsRead: contextMarkAllAsRead,
    removeNotification: contextRemoveNotification,
    clearAllNotifications: contextClearAll
  } = useNotificationContext();

  // Combinar as notificações
  const allNotifications = [...contextNotifications, ...systemNotifications];
  const totalUnreadCount = contextUnreadCount + systemUnreadCount;
  const isLoading = systemLoading;

  const handleMarkAsRead = (notificationId: string) => {
    // Tentar primeiro no contexto, depois no sistema
    if (contextNotifications.some(n => n.id === notificationId)) {
      contextMarkAsRead(notificationId);
    } else {
      systemMarkAsRead(notificationId);
    }
  };

  const handleMarkAllAsRead = () => {
    contextMarkAllAsRead();
    systemMarkAllAsRead();
  };

  const handleDeleteNotification = (notificationId: string) => {
    // Tentar primeiro no contexto, depois no sistema
    if (contextNotifications.some(n => n.id === notificationId)) {
      contextRemoveNotification(notificationId);
    } else {
      systemDeleteNotification(notificationId);
    }
  };

  useEffect(() => {
    setIsNotificationSupported(notificationService.getIsSupported());
    setHasNotificationPermission(notificationService.isPermissionGranted());
  }, []);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }
    
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    
    setIsOpen(false);
  };

  const handleDeleteNotificationClick = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    handleDeleteNotification(notificationId);
  };

  const requestNotificationPermission = async () => {
    setIsTestingNotification(true);
    try {
      const granted = await notificationService.requestPermission();
      setHasNotificationPermission(granted);
      
      if (granted) {
        toast.success('Permissão de notificação concedida! Agora você pode testar notificações nativas.');
      } else {
        toast.error('Permissão negada. Verifique as configurações do navegador.');
      }
    } catch (error) {
      toast.error('Erro ao solicitar permissão de notificação');
    } finally {
      setIsTestingNotification(false);
    }
  };

  const triggerPushTest = async (label: string) => {
    console.log('[Notifications] triggerPushTest chamado', { label, hasNotificationPermission, isIOSDevice, isStandalone });

    if (!hasNotificationPermission) {
      toast.error('Permissão de notificação necessária');
      return;
    }

    setIsTestingNotification(true);
    try {
      // Para iOS, garantir que está instalado como PWA
      if (isIOSDevice && !isStandalone) {
        setShowIOSGuide(true);
        return;
      }

      console.log('[Notifications] Enviando POST /api/notifications/test');

      const res = await fetch('/api/notifications/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payload: {
            title: 'Juris Hub',
            message: `Notificação de teste (${label}) enviada com sucesso!`,
            id: `test-${label}-${Date.now()}`,
            priority: 'normal',
          },
        }),
      });

      console.log('[Notifications] Resposta /api/notifications/test', res.status);

      if (!res.ok) {
        throw new Error(`Falha ao enviar teste (${res.status})`);
      }

      // Tentar ler o corpo para ver quantos endpoints receberam
      let body: any = null;
      try {
        body = await res.json();
        console.log('[Notifications] Corpo resposta /api/notifications/test', body);
      } catch {
        // ignore
      }

      // Notificação local imediata para garantir feedback visual no navegador atual
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Juris Hub - Teste', {
          body: `Notificação de teste (${label}) recebida neste dispositivo.`,
          icon: '/icons/icon-192x192.png',
          tag: `local-test-${label}`,
        });
      }

      const sent = body?.sent ?? undefined;
      if (typeof sent === 'number') {
        if (sent === 0) {
          toast.warning(`Requisição enviada, mas nenhum dispositivo inscrito recebeu push (sent = 0).`);
        } else {
          toast.success(`Push de teste (${label}) enviado para ${sent} dispositivo(s).`);
        }
      } else {
        toast.success(`Notificação de teste (${label}) enviada. Verifique no dispositivo.`);
      }
    } catch (error: any) {
      console.error('Erro ao testar notificação:', error);
      toast.error(error?.message || 'Erro ao enviar notificação de teste');
    } finally {
      setIsTestingNotification(false);
    }
  };

  const testQuickNotification = async (seconds: number, title: string) => {
    if (!hasNotificationPermission) {
      toast.error('Permissão de notificação necessária');
      return;
    }

    setIsTestingNotification(true);
    
    try {
      // Para iOS, garantir que está instalado como PWA
      if (isIOSDevice && !isStandalone) {
        setShowIOSGuide(true);
        setIsTestingNotification(false);
        return;
      }

      // Formatar tempo para exibição
      const timeLabel = seconds < 60 
        ? `${seconds} segundo${seconds > 1 ? 's' : ''}`
        : seconds < 3600
        ? `${Math.floor(seconds / 60)} minuto${Math.floor(seconds / 60) > 1 ? 's' : ''}`
        : `${Math.floor(seconds / 3600)} hora${Math.floor(seconds / 3600) > 1 ? 's' : ''}`;

      // Mostrar toast informando que foi agendado
      toast.info(`Notificação agendada para ${timeLabel}`);

      // Agendar para enviar após o delay
      setTimeout(async () => {
        try {
          console.log(`[Notifications] Enviando teste agendado (${title}) após ${seconds}s`);
          
          const res = await fetch('/api/notifications/test', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              payload: {
                title: 'Juris Hub - Teste',
                message: `Notificação de teste (${title}) enviada após ${timeLabel}!`,
                id: `test-${title}-${Date.now()}`,
                priority: 'normal',
              },
            }),
          });

          if (!res.ok) {
            throw new Error(`Falha ao enviar teste (${res.status})`);
          }

          const body = await res.json();
          const sent = body?.sent ?? 0;

          if (sent > 0) {
            toast.success(`Push de teste (${title}) enviado para ${sent} dispositivo(s).`);
          } else {
            toast.warning(`Requisição enviada, mas nenhum dispositivo inscrito recebeu push.`);
          }

          // Notificação local para feedback visual
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Juris Hub - Teste', {
              body: `Notificação de teste (${title}) recebida após ${timeLabel}.`,
              icon: '/icons/icon-192x192.png',
              tag: `test-${title}-${Date.now()}`,
            });
          }
        } catch (error: any) {
          console.error(`Erro ao enviar teste agendado (${title}):`, error);
          toast.error(`Erro ao enviar notificação de teste (${title})`);
        } finally {
          setIsTestingNotification(false);
        }
      }, seconds * 1000);

      // Manter o estado de "agendando" por um tempo curto para feedback visual
      // Mas não bloquear outros botões por muito tempo
      setTimeout(() => {
        setIsTestingNotification(false);
      }, 1000);
    } catch (error: any) {
      console.error('Erro ao agendar teste:', error);
      toast.error('Erro ao agendar notificação de teste');
      setIsTestingNotification(false);
    }
  };

  const [showTests, setShowTests] = useState(false);
  const testChip = 'rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-600 transition-colors hover:border-[#c4ab83] hover:text-[#7a6440] disabled:opacity-50';

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className={cn("relative", className)}>
          <Bell className="h-5 w-5" />
          {totalUnreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#c4ab83] px-1 text-[10px] font-semibold tabular-nums text-slate-900 ring-2 ring-white">
              {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-[22rem] max-w-[calc(100vw-1rem)] overflow-hidden rounded-2xl border border-[#c4ab83]/30 p-0 shadow-2xl sm:w-96"
        sideOffset={8}
      >
        {/* Cabeçalho */}
        <div className="relative overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] px-4 py-4">
          <img
            src="/imgs/logos allana/letra_A_logo.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -right-3 top-1/2 h-24 w-24 -translate-y-1/2 select-none object-contain opacity-[0.12]"
          />
          <div className="relative flex items-end justify-between gap-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Central</p>
              <h3 className="text-lg font-semibold tracking-tight text-slate-900">Notificações</h3>
              <p className="text-xs text-slate-500">
                {totalUnreadCount > 0
                  ? `${totalUnreadCount} ${totalUnreadCount > 1 ? 'novas' : 'nova'}`
                  : 'Tudo em dia'}
              </p>
            </div>
            {totalUnreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                className="flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-medium text-[#7a6440] hover:bg-[#c4ab83]/15"
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Marcar todas como lidas
              </button>
            )}
          </div>
        </div>

        {/* Lista */}
        <ScrollArea className="max-h-[55vh]">
          {isLoading ? (
            <div className="px-6 py-10 text-center">
              <div className="mx-auto mb-2 h-5 w-5 animate-spin rounded-full border-2 border-[#c4ab83] border-t-transparent" />
              <p className="text-xs text-slate-400">Carregando...</p>
            </div>
          ) : allNotifications.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-[#faf7f2]">
                <Bell className="h-5 w-5 text-[#c4ab83]" />
              </div>
              <p className="mt-3 text-sm font-medium text-slate-700">Nenhuma notificação</p>
              <p className="mt-0.5 text-xs text-slate-400">Avisos de prazos, compromissos e pagamentos aparecem aqui.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {allNotifications.map((notification) => (
                <DropdownMenuItem
                  key={notification.id}
                  className={cn(
                    "group cursor-pointer rounded-none px-4 py-3 focus:bg-[#faf7f2]",
                    !notification.isRead && "bg-[#faf7f2]/70"
                  )}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex w-full items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white">
                      {getNotificationIcon(notification.type, notification.icon)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className={cn("line-clamp-2 text-sm text-slate-800", !notification.isRead ? "font-semibold" : "font-medium")}>
                          {notification.title}
                        </p>
                        <div className="flex shrink-0 items-center gap-1">
                          {!notification.isRead && <span className="h-2 w-2 rounded-full bg-[#c4ab83]" />}
                          <button
                            type="button"
                            className="rounded-md p-0.5 text-slate-300 opacity-100 transition-opacity hover:bg-slate-100 hover:text-slate-600 md:opacity-0 md:group-hover:opacity-100"
                            onClick={(e) => handleDeleteNotificationClick(e, notification.id)}
                            title="Remover"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      {notification.message && (
                        <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{notification.message}</p>
                      )}
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-[11px] text-slate-400">{getTimeAgo(notification.createdAt)}</span>
                        {(notification.priority === 'urgent' || notification.priority === 'high') && (
                          <span className={cn(
                            "rounded-full px-1.5 py-px text-[10px] font-semibold",
                            notification.priority === 'urgent' ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"
                          )}>
                            {notification.priority === 'urgent' ? 'Urgente' : 'Importante'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Rodapé */}
        <div className="border-t border-slate-100 bg-slate-50/60">
          <button
            type="button"
            onClick={() => {
              navigate('/notifications');
              setIsOpen(false);
            }}
            className="flex w-full items-center justify-center gap-1 px-4 py-2.5 text-[13px] font-semibold text-[#7a6440] hover:bg-[#faf7f2]"
          >
            Ver todas
            <ChevronRight className="h-4 w-4" />
          </button>

          {isIOSDevice && !isStandalone && (
            <button
              type="button"
              onClick={() => {
                setShowIOSGuide(true);
                setIsOpen(false);
              }}
              className="flex w-full items-center justify-center gap-2 border-t border-slate-100 px-4 py-2.5 text-[13px] text-amber-700 hover:bg-amber-50"
            >
              <Smartphone className="h-4 w-4" />
              Instalar o app no iPhone
            </button>
          )}

          {/* Avisos no aparelho */}
          <div className="border-t border-slate-100 px-4 py-2.5">
            {!isNotificationSupported ? (
              <p className="text-[11px] text-slate-400">Este navegador não mostra avisos no aparelho.</p>
            ) : !hasNotificationPermission ? (
              <button
                type="button"
                onClick={requestNotificationPermission}
                disabled={isTestingNotification}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-2 text-[13px] font-medium text-white hover:bg-slate-800 disabled:opacity-60"
              >
                <Bell className="h-3.5 w-3.5 text-[#e8d5a8]" />
                {isTestingNotification ? 'Solicitando...' : 'Ativar avisos neste aparelho'}
              </button>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Avisos ativos neste aparelho
                  </span>
                  <button
                    type="button"
                    disabled={allNotifications.length === 0}
                    onClick={(e) => {
                      e.preventDefault();
                      contextClearAll();
                      systemClearAll();
                    }}
                    className="flex items-center gap-1 text-[11px] font-medium text-slate-400 hover:text-[#7a6440] disabled:pointer-events-none disabled:opacity-40"
                  >
                    <Trash2 className="h-3 w-3" />
                    Limpar
                  </button>
                </div>
                {showTests && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <button type="button" className={testChip} disabled={isTestingNotification} onClick={() => triggerPushTest('Imediato')}>
                      {isTestingNotification ? 'Enviando...' : 'Agora'}
                    </button>
                    <button type="button" className={testChip} disabled={isTestingNotification} onClick={() => testQuickNotification(10, '10s')}>Em 10s</button>
                    <button type="button" className={testChip} disabled={isTestingNotification} onClick={() => testQuickNotification(60, '1min')}>Em 1 min</button>
                    <button type="button" className={testChip} disabled={isTestingNotification} onClick={() => testQuickNotification(300, '5min')}>Em 5 min</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </DropdownMenuContent>

      {/* Guia de instalação iOS */}
      {showIOSGuide && (
        <IOSPWAInstallGuide
          isOpen={showIOSGuide}
          onClose={() => setShowIOSGuide(false)}
        />
      )}
    </DropdownMenu>
  );
};
